import type { Distance, Point, SpatialRectangle } from "@pdfking/math"

import type { SymmetryAxis } from "#public"

import type { InternalMatrixBlock } from "../types/matrix"
import type { FillNodeData } from "../types/node"
import {
  plot_origin,
  plot_total_size,
  point_len,
  subjects_flipped_total_size,
  subjects_plot_diff,
} from "./Geometry"

/**
 * Mirrors Python `FillStrategy._internal_matrix` (strategy.py:1086-1182).
 *
 * Yields one or more `(rotated, matrix, offset, open)` blocks: the
 * first is the main (non-rotated) subjects grid, then up to two blocks
 * of flipped subjects per axis (depending on `subjects_flipped` and
 * `symmetric_grow`). The `_free_space` closure picks which edges of each
 * flipped block face the empty sheet, based on whether it sits before
 * (`o.x/y <= main.x/y`) or after the main block.
 *
 * Departs from Python in two places: a strip that does not fill its axis is
 * one block against the origin edge, not two halves at either end; and an open
 * edge carries no bleed of its own — how far a page may bleed into the empty
 * sheet is the plot's to cap, not the fill's.
 */
export const internal_matrix = (
  data: FillNodeData,
  position: SpatialRectangle<Distance>
): InternalMatrixBlock[] => {
  const sym_molt: Point<number> = {
    x: (data.symmetric_grow.x ? 1 : 0) + 1,
    y: (data.symmetric_grow.y ? 1 : 0) + 1,
  }
  const flip_size = subjects_flipped_total_size(data)
  const origin = plot_origin(data, position)
  const total = plot_total_size(data)
  const diff = subjects_plot_diff(data)

  // Python: main = XYMatrix(x=plot_origin.x + flip_size.x.x,
  //                         y=plot_origin.y + flip_size.y.y) + subjects_plot_diff
  const main: Point<Distance> = {
    x: origin.x + flip_size.x.x + diff.x,
    y: origin.y + flip_size.y.y + diff.y,
  } as Point<Distance>

  const out: InternalMatrixBlock[] = []

  // First yield: the main subjects block.
  out.push({
    rotated: false,
    matrix: {
      x: data.subjects.x * sym_molt.x,
      y: data.subjects.y * sym_molt.y,
    },
    offset: main,
    open: { top: true, right: true, bottom: true, left: true },
  })

  // Per-axis flipped blocks.
  const axes = [
    { a1: "x", a2: "y" },
    { a1: "y", a2: "x" },
  ] as const

  for (const { a1, a2 } of axes) {
    const flipped_on_a1 = data.subjects_flipped[a1]
    if (!point_len(flipped_on_a1)) continue

    // Python: a1_offsets = (origin[a1], origin[a1] + plot_total[a1] - flip_size[a1][a1])
    //   if symmetric_grow[a1] else (origin[a1],)
    const a1_offsets: number[] = data.symmetric_grow[a1]
      ? [origin[a1], origin[a1] + total[a1] - flip_size[a1][a1]]
      : [origin[a1]]

    for (const a1_offset of a1_offsets) {
      // Python: m1 = subjects_flipped[a1] * XYMatrix(**{a2: sym_molt[a2], a1: 1})
      const m1_template: Point<number> =
        a1 === "x" ? { x: 1, y: sym_molt[a2] } : { x: sym_molt[a2], y: 1 }
      const m1: Point<number> = {
        x: flipped_on_a1.x * m1_template.x,
        y: flipped_on_a1.y * m1_template.y,
      }

      // One block, against the origin edge. Python split a strip that did not
      // fill the axis in two, one half at each end (strategy.py:1157-1179);
      // that costs a cut, so the strip stays whole where the fill starts and
      // the leftover is all on its far side.
      const offset: Point<Distance> = (
        a1 === "x" ? { x: a1_offset, y: origin[a2] } : { x: origin[a2], y: a1_offset }
      ) as Point<Distance>
      out.push(_free_space(m1, offset, main, a1))
    }
  }

  return out
}

/**
 * Mirrors Python `_free_space(m, o, overrides)` (strategy.py:1120-1137), as
 * which edges are open rather than how far they bleed:
 *
 *   if a1 == "x":
 *       margins = {
 *           "margin_top": bleed, "margin_bottom": bleed,
 *           "margin_left": o.x <= main.x and bleed or D(),
 *           "margin_right": o.x > main.x and bleed or D(),
 *       }
 *   else:
 *       margins = {
 *           "margin_left": abs(o.x - self.x1),
 *           "margin_right": abs(o.x - self.x1),
 *           "margin_top": o.y <= main.y and bleed or D(),
 *           "margin_bottom": o.y > main.y and bleed or D(),
 *       }
 *   return True, m, o, MarginMeasureSet(**margins)
 *
 * Python also merged per-split overrides in; the strip is no longer split.
 */
const _free_space = (
  matrix: Point<number>,
  offset: Point<Distance>,
  main: Point<Distance>,
  a1: SymmetryAxis
): InternalMatrixBlock => ({
  rotated: true,
  matrix,
  offset,
  open:
    a1 === "x"
      ? { top: true, bottom: true, left: offset.x <= main.x, right: offset.x > main.x }
      : {
          left: true,
          right: true,
          // The block that sits further from the origin is the upper one: y
          // grows up, so it is the one whose outer edge is the top.
          top: offset.y >= main.y,
          bottom: offset.y < main.y,
        },
})
